import { useCallback, useEffect, useRef, useState } from "react";
import { getDrawpostApiUrl } from "../../config/api";
import { authFetch } from "../../config/authFetch";
import { useApi } from "../../context/ApiContext";

type QueryValue = string | number | undefined;

interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

// Shares one request between StrictMode's double effect run and repeat tab visits.
const CACHE_TTL_MS = 60_000;
const responseCache = new Map<string, { at: number; promise: Promise<unknown> }>();

const requestSection = async (url: string): Promise<unknown> => {
  const response = await authFetch(url, { headers: { accept: "application/json" } });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.status !== "success") {
    throw new Error(result.message || `HTTP ${response.status}`);
  }
  return result.data;
};

const cachedRequest = (url: string, force: boolean): Promise<unknown> => {
  const hit = responseCache.get(url);
  if (!force && hit && Date.now() - hit.at < CACHE_TTL_MS) return hit.promise;
  const promise = requestSection(url).catch((err) => {
    responseCache.delete(url);
    throw err;
  });
  responseCache.set(url, { at: Date.now(), promise });
  return promise;
};

/** Loads `/admin/users/:userId[/<section>]`; refetches when the query or env changes. */
export function useUserSection<T>(
  userId: number,
  section: string,
  query: Record<string, QueryValue> = {}
) {
  const { refreshTrigger } = useApi();
  const [state, setState] = useState<SectionState<T>>({ data: null, loading: true, error: null });
  const latestRequest = useRef(0);

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, String(value));
  }
  const queryString = params.toString();

  const load = useCallback(
    async (force: boolean) => {
      const requestId = ++latestRequest.current;
      const base = section ? `/api/v1.0.0/admin/users/${userId}/${section}` : `/api/v1.0.0/admin/users/${userId}`;
      const url = getDrawpostApiUrl(queryString ? `${base}?${queryString}` : base);
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const data = (await cachedRequest(url, force)) as T;
        if (requestId === latestRequest.current) setState({ data, loading: false, error: null });
      } catch (err) {
        if (requestId === latestRequest.current) {
          setState({ data: null, loading: false, error: err instanceof Error ? err.message : "Failed to load" });
        }
      }
    },
    [userId, section, queryString]
  );

  const seenTrigger = useRef(refreshTrigger);
  useEffect(() => {
    // Only an env switch / manual refresh bypasses the cache.
    const force = seenTrigger.current !== refreshTrigger;
    seenTrigger.current = refreshTrigger;
    load(force);
  }, [load, refreshTrigger]);

  const reload = useCallback(() => load(true), [load]);
  return { ...state, reload };
}
